// src/components/notify/console/NotifyStatsStrip.js
// KPI tiles for the Notification Command Center.
import React, { memo } from 'react';
import { Box, Flex, Icon, SimpleGrid, Skeleton, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';
import {
    MdCheckCircle, MdDoneAll, MdErrorOutline, MdPeople, MdSchedule, MdSend, MdTrendingUp, MdWifiTethering,
} from 'react-icons/md';
import { fmtDuration, pct } from '../notifyFormat';

const Tile = memo(function Tile({ label, value, sub, icon, scheme, loading, tip, onClick }) {
    const bg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const iconBg = useColorModeValue(`${scheme}.50`, `${scheme}.900`);
    const body = (
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} h="100%" w="100%" textAlign="left"
            as={onClick ? 'button' : 'div'} type={onClick ? 'button' : undefined} onClick={onClick} _hover={onClick ? { borderColor: `${scheme}.300` } : undefined}>
            <Flex justify="space-between" align="flex-start">
                <Box minW={0}>
                    <Text fontSize="xs" fontWeight="700" color={muted} textTransform="uppercase" letterSpacing="wide">{label}</Text>
                    {loading ? <Skeleton h="28px" w="56px" mt={1} /> : <Text fontSize="2xl" fontWeight="800" lineHeight="1.2">{value}</Text>}
                    {sub && <Text fontSize="xs" color={muted} mt={0.5} noOfLines={2}>{sub}</Text>}
                </Box>
                <Flex bg={iconBg} borderRadius="lg" p={2} flexShrink={0}><Icon as={icon} color={`${scheme}.500`} boxSize={5} /></Flex>
            </Flex>
        </Box>
    );
    return tip ? <Tooltip label={tip} hasArrow openDelay={300}>{body}</Tooltip> : body;
});

function NotifyStatsStrip({ stats, loading, onShowScheduled, onShowFailed }) {
    const s = stats || {};
    const live = s.live || {};
    return (
        <SimpleGrid columns={{ base: 2, md: 4, xl: 8 }} spacing={3}>
            <Tile label="Sent" value={s.sent ?? '—'} sub={`${s.days ?? 7} days · ${s.dispatches ?? 0} dispatches`} icon={MdSend} scheme="purple" loading={loading} />
            <Tile label="Recipients" value={s.recipients ?? '—'} sub={`${s.inbox ?? 0} inbox deliveries`} icon={MdPeople} scheme="blue" loading={loading} />
            <Tile label="Delivered" value={pct(s.delivery_rate)} sub="reached a live device or was read" icon={MdCheckCircle} scheme="green" loading={loading} tip="Delivered = shown on an open web/tech app session, or read" />
            <Tile label="Read rate" value={pct(s.read_rate)} sub={`avg time to read ${fmtDuration(s.avg_read_sec)}`} icon={MdDoneAll} scheme="teal" loading={loading} />
            <Tile label="Acknowledged" value={pct(s.ack_rate)} sub={`${s.ack_pending ?? 0} still waiting · ${s.escalated ?? 0} escalated`} icon={MdTrendingUp} scheme={s.ack_pending ? 'red' : 'green'} loading={loading} />
            <Tile label="Scheduled" value={s.scheduled ?? '—'} sub="waiting to go out" icon={MdSchedule} scheme="cyan" loading={loading} onClick={onShowScheduled} tip="Open scheduled notifications" />
            <Tile label="Failed" value={(s.failed ?? 0) + (s.partial ?? 0)} sub={`${s.channel_failures ?? 0} channel errors`} icon={MdErrorOutline} scheme={(s.failed || s.partial) ? 'red' : 'gray'} loading={loading} onClick={onShowFailed} />
            <Tile label="Online now" value={live.users_online ?? '—'} sub={`${live.connections ?? 0} live · ${s.mobile_push ?? 0} mobile push${s.fcm?.ready ? '' : ' (off)'}`} icon={MdWifiTethering} scheme="green" loading={loading} tip={`Browser push: ${s.web_push?.ready ? 'ready' : s.web_push?.reason || 'off'} · Mobile push (FCM): ${s.fcm?.ready ? 'ready' : s.fcm?.reason || 'off'}`} />{/* --- UPDATE (FCM channel) */}
        </SimpleGrid>
    );
}

export default memo(NotifyStatsStrip);
